import { useEffect, useRef, useState, useCallback } from "react";
import Quagga from "@ericblade/quagga2";

// VIN barcodes on vehicles are Code 39 (pre-2000s) or Code 128 (modern)
// Located on: door jamb sticker, dashboard (driver side), windshield sticker

const scannerStyles = `
  .vscan-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.92);
    z-index: 9999; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
  }
  .vscan-modal {
    width: min(480px, 95vw);
    background: #161B27;
    border: 1px solid #252D3D;
    border-radius: 16px;
    overflow: hidden;
    display: flex; flex-direction: column;
  }
  .vscan-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #252D3D;
  }
  .vscan-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 20px; letter-spacing: 2px; color: #F1F5F9;
    display: flex; align-items: center; gap: 10px;
  }
  .vscan-close {
    background: transparent; border: 1px solid #252D3D;
    border-radius: 6px; width: 30px; height: 30px;
    color: #94A3B8; cursor: pointer; font-size: 16px;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
  }
  .vscan-close:hover { border-color: #EF4444; color: #EF4444; }
  .vscan-viewport {
    position: relative; width: 100%;
    background: #000; aspect-ratio: 4/3; overflow: hidden;
  }
  .vscan-viewport video {
    width: 100%; height: 100%; object-fit: cover; display: block;
  }
  .vscan-viewport canvas {
    position: absolute; top: 0; left: 0;
    width: 100%; height: 100%;
  }
  .vscan-aim {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none;
  }
  .vscan-aim-box {
    width: 80%; height: 22%;
    border: 2px solid #F5A623;
    border-radius: 6px;
    box-shadow: 0 0 0 2000px rgba(0,0,0,0.45);
    position: relative;
  }
  .vscan-aim-line {
    position: absolute; left: 4px; right: 4px; top: 50%;
    height: 2px; background: rgba(245,166,35,0.7);
    transform: translateY(-50%);
    animation: scanline 1.8s ease-in-out infinite;
  }
  .vscan-corner {
    position: absolute; width: 16px; height: 16px;
    border-color: #F5A623; border-style: solid;
  }
  .vscan-corner-tl { top: -2px; left: -2px; border-width: 3px 0 0 3px; border-radius: 3px 0 0 0; }
  .vscan-corner-tr { top: -2px; right: -2px; border-width: 3px 3px 0 0; border-radius: 0 3px 0 0; }
  .vscan-corner-bl { bottom: -2px; left: -2px; border-width: 0 0 3px 3px; border-radius: 0 0 0 3px; }
  .vscan-corner-br { bottom: -2px; right: -2px; border-width: 0 3px 3px 0; border-radius: 0 0 3px 0; }
  .vscan-footer { padding: 14px 20px; display: flex; flex-direction: column; gap: 10px; }
  .vscan-status {
    text-align: center; font-size: 12px; font-weight: 600;
    letter-spacing: 0.5px; min-height: 18px;
    transition: color 0.2s;
  }
  .vscan-status.scanning { color: #94A3B8; }
  .vscan-status.found    { color: #22C55E; }
  .vscan-status.error    { color: #EF4444; }
  .vscan-hint {
    font-size: 11px; color: #475569; text-align: center; line-height: 1.5;
  }
  .vscan-hint strong { color: #64748B; }
  .vscan-manual-btn {
    background: transparent; border: 1px solid #252D3D;
    border-radius: 8px; padding: 9px; font-size: 12px;
    color: #64748B; cursor: pointer; font-family: 'DM Sans', sans-serif;
    transition: all 0.15s; text-align: center;
  }
  .vscan-manual-btn:hover { border-color: #94A3B8; color: #94A3B8; }
  @keyframes scanline {
    0%, 100% { top: 15%; opacity: 0.5; }
    50%       { top: 85%; opacity: 1;   }
  }
`;

function isValidVin(str) {
  // Basic VIN validation: 17 chars, alphanumeric, no I/O/Q
  return /^[A-HJ-NPR-Z0-9]{17}$/.test(str);
}

export default function VinScanner({ onDetected, onClose }) {
  const scannerRef  = useRef(null);
  const lastScan    = useRef("");
  const confirmBuf  = useRef([]);
  const quaggaLive  = useRef(false);
  const [status,    setStatus]  = useState("scanning");
  const [statusMsg, setStatusMsg] = useState("Aim camera at VIN barcode");
  const [camError,  setCamError]  = useState(false);

  const handleDetected = useCallback((result) => {
    const raw = (result?.codeResult?.code || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

    // Must be 17 chars and pass VIN format check
    if (!isValidVin(raw)) return;

    // Require 2 consecutive matching reads to avoid false positives
    confirmBuf.current.push(raw);
    if (confirmBuf.current.length > 3) confirmBuf.current.shift();

    const confirmed = confirmBuf.current.filter(v => v === raw).length >= 2;
    if (!confirmed || raw === lastScan.current) return;

    lastScan.current = raw;
    setStatus("found");
    setStatusMsg(`✓ VIN captured: ${raw}`);

    // Brief pause so user sees the success state, then close
    setTimeout(() => {
      stopScanner();
      onDetected(raw);
    }, 700);
  }, [onDetected]);

  const startScanner = useCallback(() => {
    if (!scannerRef.current) return;

    Quagga.init(
      {
        inputStream: {
          type: "LiveStream",
          target: scannerRef.current,
          constraints: {
            width:  { ideal: 1280 },
            height: { ideal: 720  },
            facingMode: "environment",   // rear camera on mobile
            aspectRatio: { ideal: 16/9 },
          },
        },
        locator: {
          patchSize: "medium",
          halfSample: true,
        },
        numOfWorkers: 2,
        frequency: 10,
        decoder: {
          readers: [
            "code_128_reader",   // modern vehicles (2000+)
            "code_39_reader",    // older vehicles
          ],
          multiple: false,
        },
        locate: true,
      },
      (err) => {
        if (err) {
          console.error("Quagga init error:", err);
          setCamError(true);
          setStatus("error");
          setStatusMsg("Camera access denied or unavailable");
          return;
        }
        quaggaLive.current = true;
        Quagga.start();
        Quagga.onDetected(handleDetected);
      }
    );
  }, [handleDetected]);

  const stopScanner = useCallback(() => {
    if (quaggaLive.current) {
      try {
        Quagga.offDetected(handleDetected);
        Quagga.stop();
      } catch (e) { /* ignore */ }
      quaggaLive.current = false;
    }
  }, [handleDetected]);

  // Start on mount, stop on unmount
  useEffect(() => {
    // Small delay to let modal render first
    const t = setTimeout(startScanner, 100);
    return () => {
      clearTimeout(t);
      stopScanner();
    };
  }, [startScanner, stopScanner]);

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  return (
    <>
      <style>{scannerStyles}</style>
      <div className="vscan-overlay" onClick={handleClose}>
        <div className="vscan-modal" onClick={e => e.stopPropagation()}>

          <div className="vscan-header">
            <div className="vscan-title">
              📷 VIN Scanner
            </div>
            <button className="vscan-close" onClick={handleClose}>✕</button>
          </div>

          <div className="vscan-viewport" ref={scannerRef}>
            {/* Quagga renders video + canvas here */}
            {!camError && (
              <div className="vscan-aim">
                <div className="vscan-aim-box">
                  <div className="vscan-corner vscan-corner-tl" />
                  <div className="vscan-corner vscan-corner-tr" />
                  <div className="vscan-corner vscan-corner-bl" />
                  <div className="vscan-corner vscan-corner-br" />
                  <div className="vscan-aim-line" />
                </div>
              </div>
            )}
            {camError && (
              <div style={{
                position:"absolute", inset:0, display:"flex",
                alignItems:"center", justifyContent:"center",
                flexDirection:"column", gap:12, padding:24, textAlign:"center"
              }}>
                <div style={{fontSize:40}}>📷</div>
                <div style={{color:"#EF4444", fontSize:13, fontWeight:600}}>
                  Camera not available
                </div>
                <div style={{color:"#475569", fontSize:11, lineHeight:1.6}}>
                  Allow camera access in your browser settings, or use a device with a camera.
                </div>
              </div>
            )}
          </div>

          <div className="vscan-footer">
            <div className={`vscan-status ${status}`}>{statusMsg}</div>
            <div className="vscan-hint">
              Point at the <strong>VIN barcode</strong> on the <strong>driver's door jamb</strong> or <strong>dashboard</strong>.
              Hold steady — the scanner reads Code 128 and Code 39 barcodes.
            </div>
            <button className="vscan-manual-btn" onClick={handleClose}>
              Enter VIN manually instead
            </button>
          </div>

        </div>
      </div>
    </>
  );
}
